import { useMemo } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import {
  collection,
  query,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import { createBatchedWriter } from '@/lib/cascade';
import { useAuth } from '../../auth/hooks/useAuth';
import type { Contact, ContactInput } from '@elka/types';

export function useContacts() {
  const { user } = useAuth();

  // Query pattern same as EM's useClients - unconditional.
  // Memoized so the query reference is stable across renders — otherwise a fresh
  // query() object every render churns useCollection's subscription.
  const contactsQuery = useMemo(
    () =>
      query(collection(db, ...splitPath(COLLECTIONS.crm.contacts)), orderBy('createdAt', 'desc')),
    []
  );

  const [snapshot, loading, error] = useCollection(contactsQuery);

  const contacts: Contact[] =
    (snapshot?.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Contact[]) || [];

  // Add contact
  const addContact = async (data: ContactInput) => {
    if (!user) throw new Error('לא מחובר');

    const timestamp = serverTimestamp();
    const userInfo = {
      userId: user.uid,
      createdBy: user.displayName || user.email || 'Unknown',
      createdAt: timestamp,
    };

    const docRef = await addDoc(collection(db, ...splitPath(COLLECTIONS.crm.contacts)), {
      ...data,
      ...userInfo,
    });

    return docRef.id;
  };

  // Update contact. When the display name changes, fan the new value out to every
  // doc that denormalizes it (deals/activities/tasks store contactName, and it's a
  // search key) so those records don't keep showing — and stay findable by — the old name.
  const updateContact = async (id: string, data: Partial<ContactInput>) => {
    if (!user) throw new Error('לא מחובר');

    const existing = contacts.find((c) => c.id === id);
    const renamed = !!(
      data.name !== undefined &&
      existing &&
      data.name &&
      existing.name !== data.name
    );

    const timestamp = serverTimestamp();
    const updateInfo = {
      updatedBy: user.displayName || user.email || 'Unknown',
      updatedAt: timestamp,
    };

    // ponytail: on a rename, fan out to the children BEFORE writing the parent's
    // new name (same recovery guarantee as useCompanies). If any child read/write
    // throws, the contact stays on the OLD name, so `renamed` still evaluates true
    // on a retry and the whole fan-out re-runs. Ceiling: children update in separate
    // batches, so a partial-batch failure still needs a retry to converge.
    if (renamed) {
      const writer = createBatchedWriter();
      const childFan = { contactName: data.name!, ...updateInfo };
      const childCollections: string[] = [
        COLLECTIONS.crm.deals,
        COLLECTIONS.crm.activities,
        COLLECTIONS.crm.tasks,
      ];
      for (const collectionPath of childCollections) {
        const docs = await getDocs(
          query(collection(db, ...splitPath(collectionPath)), where('contactId', '==', id))
        );
        for (const d of docs.docs) {
          writer.update(doc(db, ...splitPath(collectionPath), d.id), childFan);
        }
      }
      await writer.commit();
    }

    await updateDoc(doc(db, ...splitPath(COLLECTIONS.crm.contacts), id), {
      ...data,
      ...updateInfo,
    });
  };

  // Delete contact
  const deleteContact = async (id: string) => {
    if (!user) throw new Error('לא מחובר');
    await deleteDoc(doc(db, ...splitPath(COLLECTIONS.crm.contacts), id));
  };

  return {
    contacts,
    isLoading: loading,
    error: error?.message || null,
    addContact,
    updateContact,
    deleteContact,
  };
}
